import { resolveEcwStaffId } from "@/src/lib/ehr/ecw/staff-match";
import type { EcwStaff } from "@/src/lib/ehr/ecw/webecw/endpoints/staff";

export enum AssigneeUnresolvedReason {
  NO_ASSIGNMENT = "no_assignment",
  ROSTER_GAP = "roster_gap",
}

/**
 * Outcome of resolving a document's intended assignee to an eCW staff id.
 * Shared across webecw tenants (Autonomy, USHV MKC) and persisted as the
 * `resolve_assignee` step output. A roster gap carries the intended name so
 * downstream surfaces (e.g. document-management instructions) can show it.
 */
export type AssigneeResolution =
  | { resolved: true; staffId: string; name: string }
  | { resolved: false; reason: AssigneeUnresolvedReason.NO_ASSIGNMENT }
  | { resolved: false; reason: AssigneeUnresolvedReason.ROSTER_GAP; intendedName: string };

export type NamedAssigneeResolution = { resolved: true; staffId: string; name: string } | { resolved: false };

export function resolveNamedAssignee(
  name: string,
  matchStaffId: (name: string) => string | null,
  lookupName: (staffId: string) => string | null
): NamedAssigneeResolution {
  const staffId = matchStaffId(name);

  if (!staffId) return { resolved: false };

  return { resolved: true, staffId, name: lookupName(staffId) ?? name };
}

/**
 * A document's intended assignee before it's matched against the eCW staff roster: a specific
 * staff id, a name to look up, or none. Shared across webecw tenants (Autonomy, USHV MKC, USHV MCA).
 */
export type IntendedAssignee =
  | { kind: "name"; name: string }
  | { kind: "staffId"; id: string; name: string }
  | { kind: "none" };

/** Match a tenant's intended assignee to an eCW staff id. */
export function resolveWebecwAssignee(intended: IntendedAssignee, staff: EcwStaff[]): AssigneeResolution {
  if (intended.kind === "none") {
    return { resolved: false, reason: AssigneeUnresolvedReason.NO_ASSIGNMENT };
  }

  if (intended.kind === "staffId") {
    return { resolved: true, staffId: intended.id, name: intended.name };
  }

  const resolution = resolveNamedAssignee(
    intended.name,
    (name) => resolveEcwStaffId(name, staff),
    (staffId) => staff.find((entry) => entry.id === staffId)?.name ?? null
  );

  return resolution.resolved
    ? resolution
    : { resolved: false, reason: AssigneeUnresolvedReason.ROSTER_GAP, intendedName: intended.name };
}
